import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import { DownOutlined } from '@ant-design/icons';
import { TRY_UNMERGE_ISSUE_SOURCES, tryUnmergeIssueSources, UnmergeIssueSourcesMode } from '@/actions/triage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { IssueEntity } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';
import { formatDateWithTimezone } from '@/timeHelpers';
import { issueSourceDisplayText, sourceOptionLabel } from './triageHelpers';

interface UnmergeSourceModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    issue: IssueEntity;
}

export const UnmergeSourceModal: React.FC<UnmergeSourceModalProps> = ({ open, onClose, orgId, issue }) => {
    const dispatch = useDispatch();
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, issue.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, issue.appId));
    const [selectedSourceIds, setSelectedSourceIds] = React.useState<string[]>([]);
    const [markNotAPriority, setMarkNotAPriority] = React.useState(false);
    const [submittedMode, setSubmittedMode] = React.useState<UnmergeIssueSourcesMode | null>(null);

    const isSaving = useIsLoading(TRY_UNMERGE_ISSUE_SOURCES, issue.id);
    const saveError = useError(TRY_UNMERGE_ISSUE_SOURCES, issue.id);
    const saveSucceeded = useUpdated(TRY_UNMERGE_ISSUE_SOURCES, issue.id);

    React.useEffect(() => {
        if (saveSucceeded) {
            onClose();
        }
    }, [saveSucceeded, onClose]);

    React.useEffect(() => {
        if (!open) {
            setSelectedSourceIds([]);
            setMarkNotAPriority(false);
            setSubmittedMode(null);
        }
    }, [open]);

    const onSubmit = (mode: UnmergeIssueSourcesMode) => {
        setSubmittedMode(mode);
        dispatch(
            tryUnmergeIssueSources({
                orgId,
                appId: issue.appId,
                issueId: issue.id,
                sourceIds: selectedSourceIds,
                mode,
                markNotAPriority,
            }),
        );
    };

    const sources = issue.sources ?? [];
    const options = sources.map((source) => ({
        value: source.id,
        searchText: issueSourceDisplayText(source),
        label: sourceOptionLabel(
            source,
            issue.appId,
            issueSourceDisplayText(source),
            formatDateWithTimezone(source.createdAt, 'shortWithTimezone', effectiveTimezone, use24HourTimeFormat),
        ),
    }));

    const selectedCount = selectedSourceIds.length;
    return (
        <Modal
            title="Unmerge signal"
            open={open}
            onCancel={onClose}
            destroyOnClose
            width={560}
            styles={{ footer: { padding: '0 24px 16px', marginTop: 0 } }}
            footer={
                <>
                    <Button onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        disabled={selectedCount === 0 || (isSaving && submittedMode !== 'single')}
                        loading={isSaving && submittedMode === 'single'}
                        onClick={() => onSubmit('single')}
                    >
                        {selectedSourceIds.length > 1 ? 'Split all into one new issue' : 'Split into new issue'}
                    </Button>
                    {selectedCount > 1 && (
                        <Button
                            disabled={isSaving && submittedMode !== 'perSource'}
                            loading={isSaving && submittedMode === 'perSource'}
                            onClick={() => onSubmit('perSource')}
                        >
                            {`Split into ${selectedCount} new issues`}
                        </Button>
                    )}
                </>
            }
        >
            {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Select one or more signals to unmerge from this issue. You can choose to split selected signals out into
                individual issues, or all into one issue.
            </Text>
            <Select
                mode="multiple"
                allowClear
                value={selectedSourceIds}
                onChange={setSelectedSourceIds}
                options={options}
                suffixIcon={<DownOutlined className="ant-select-suffix" />}
                maxCount={Math.max(sources.length - 1, 0)}
                filterOption={(input, option) => (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())}
                placeholder="Select signals to unmerge"
                style={{ width: '100%' }}
            />
            <Checkbox
                checked={markNotAPriority}
                onChange={(e) => setMarkNotAPriority(e.target.checked)}
                style={{ marginTop: 20 }}
            >
                Mark new issue(s) as 'Not a priority'
            </Checkbox>
        </Modal>
    );
};
